import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "../context/CookieConsentContext";

const categoryContent = {
  performance: {
    label: "Performance Cookies",
    description:
      "These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site. All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies we will not know when you have visited our site, and will not be able to monitor its performance.",
  },
  necessary: {
    label: "Strictly Necessary Cookies",
    description:
      "These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work. These cookies do not store any personally identifiable information.",
  },
  targeting: {
    label: "Targeting Cookies",
    description:
      "These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites. They do not store directly personal information, but are based on uniquely identifying your browser and internet device. If you do not allow these cookies, you will experience less targeted advertising.",
  },
  functional: {
    label: "Functional Cookies",
    description:
      "These cookies enable the website to provide enhanced functionality and personalisation. They may be set by us or by third party providers whose services we have added to our pages. If you do not allow these cookies then some or all of these services may not function properly.",
  },
};

export default function CookiePreferencesModal() {
  const { consent, isPreferencesOpen, closePreferences, acceptAll, saveChoices, rejectNonEssential } =
    useCookieConsent();

  const [draft, setDraft] = useState(consent);
  const [expanded, setExpanded] = useState("performance"); // first row open by default

  useEffect(() => {
    if (isPreferencesOpen) setDraft(consent);
  }, [isPreferencesOpen, consent]);

  if (!isPreferencesOpen) return null;

  const toggle = (key) => setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleExpanded = (key) => setExpanded((prev) => (prev === key ? null : key));

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={brandStyle}>
            <div style={logoBoxStyle}>AG</div>
            <span style={brandNameStyle}>AntoGroup</span>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div style={bodyStyle}>
          <p style={introTextStyle}>
            We use cookies to personalize your experience and understand how our
            site is used. You can choose which types of cookies to allow below.
            Blocking some cookies may affect site functionality.
          </p>

          <Link
            to="/privacy-policy"
            style={moreInfoLinkStyle}
            onClick={closePreferences}
          >
            More information
          </Link>

          <button style={allowAllBtnStyle} onClick={acceptAll}>
            Allow All
          </button>

          <h3 style={sectionTitleStyle}>Manage Consent Preferences</h3>

          <CategoryRow
            categoryKey="performance"
            checked={draft.performance}
            onChange={() => toggle("performance")}
            expanded={expanded === "performance"}
            onToggleExpand={() => toggleExpanded("performance")}
          />
          <CategoryRow
            categoryKey="necessary"
            checked={true}
            alwaysActive
            expanded={expanded === "necessary"}
            onToggleExpand={() => toggleExpanded("necessary")}
          />
          <CategoryRow
            categoryKey="targeting"
            checked={draft.targeting}
            onChange={() => toggle("targeting")}
            expanded={expanded === "targeting"}
            onToggleExpand={() => toggleExpanded("targeting")}
          />
          <CategoryRow
            categoryKey="functional"
            checked={draft.functional}
            onChange={() => toggle("functional")}
            expanded={expanded === "functional"}
            onToggleExpand={() => toggleExpanded("functional")}
          />
        </div>

        {/* Footer */}
        <div style={footerWrapperStyle}>
          <div style={footerBtnRowStyle}>
            <button style={rejectBtnStyle} onClick={rejectNonEssential}>
              Reject Non-Essential
            </button>
            <button style={saveBtnStyle} onClick={() => saveChoices(draft)}>
              Save My Choices
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ categoryKey, checked, onChange, alwaysActive, expanded, onToggleExpand }) {
  const { label, description } = categoryContent[categoryKey];

  return (
    <div style={expanded ? { ...rowWrapperStyle, ...rowWrapperActiveStyle } : rowWrapperStyle}>
      <div style={rowStyle}>
        <div style={rowLeftStyle} onClick={onToggleExpand}>
          <span style={plusIconStyle}>{expanded ? "−" : "+"}</span>
          <span style={{ fontWeight: 600 }}>{label}</span>
        </div>

        {alwaysActive ? (
          <span style={alwaysActiveStyle}>Always Active</span>
        ) : (
          <label style={switchStyle}>
            <input
              type="checkbox"
              checked={checked}
              onChange={onChange}
              style={{ display: "none" }}
            />
            <span style={{ ...trackStyle, background: checked ? "#2E7D32" : "#ccc" }}>
              <span
                style={{
                  ...knobStyle,
                  transform: checked ? "translateX(20px)" : "translateX(0)",
                }}
              />
            </span>
          </label>
        )}
      </div>

      {expanded && <p style={descriptionStyle}>{description}</p>}
    </div>
  );
}

/* ---------- Styles ---------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 10000,
};

const modalStyle = {
  background: "#fff",
  borderRadius: 6,
  width: "90%",
  maxWidth: 560,
  maxHeight: "88vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  fontFamily: "Arial, sans-serif",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px",
  borderBottom: "1px solid #eee",
};

const brandStyle = { display: "flex", alignItems: "center", gap: 10 };

const logoBoxStyle = {
  width: 32,
  height: 32,
  background: "#C62828",
  color: "#fff",
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 700,
  fontSize: 13,
};

const brandNameStyle = { fontWeight: 700, fontSize: 16 };

const bodyStyle = {
  padding: "16px 20px",
  overflowY: "auto",
  flex: 1,
};

const introTextStyle = {
  fontSize: 13,
  color: "#333",
  lineHeight: 1.5,
  margin: "0 0 10px 0",
};

const moreInfoLinkStyle = {
  fontSize: 13,
  fontWeight: 600,
  textDecoration: "underline",
  color: "#000",
};

const allowAllBtnStyle = {
  display: "block",
  marginTop: 18,
  marginBottom: 20,
  background: "#2E7D32",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "10px 20px",
  fontWeight: 700,
  fontSize: 14,
  cursor: "pointer",
};

const sectionTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  margin: "0 0 12px 0",
};

const rowWrapperStyle = {
  border: "1px solid #e0e0e0",
  borderRadius: 4,
  padding: "12px 14px",
  marginBottom: 10,
};

const rowWrapperActiveStyle = {
  border: "1px solid #1a73e8",
};

const rowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const rowLeftStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
};

const plusIconStyle = {
  fontWeight: 700,
  fontSize: 16,
  width: 16,
  textAlign: "center",
};

const alwaysActiveStyle = {
  color: "#1a73e8",
  fontWeight: 600,
  fontSize: 13,
};

const descriptionStyle = {
  fontSize: 12.5,
  color: "#555",
  lineHeight: 1.5,
  marginTop: 10,
  marginLeft: 26,
};

const switchStyle = { cursor: "pointer" };

const trackStyle = {
  display: "inline-block",
  width: 40,
  height: 22,
  borderRadius: 12,
  position: "relative",
  transition: "background 0.2s",
};

const knobStyle = {
  position: "absolute",
  top: 2,
  left: 2,
  width: 18,
  height: 18,
  borderRadius: "50%",
  background: "#fff",
  transition: "transform 0.2s",
};

const footerWrapperStyle = {
  borderTop: "1px solid #eee",
};

const footerBtnRowStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  padding: "16px 20px",
};

const rejectBtnStyle = {
  background: "#C62828",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "10px 18px",
  fontWeight: 700,
  cursor: "pointer",
};

const saveBtnStyle = {
  background: "#2E7D32",
  color: "#fff",
  border: "none",
  borderRadius: 4,
  padding: "10px 18px",
  fontWeight: 700,
  cursor: "pointer",
};